import React, { useState, useEffect, useCallback } from 'react';
import { talukaAPI } from '../../services/api';

// Helper to mask phone number
function maskPhone(phone) {
  if (!phone) return '—';
  const str = String(phone).replace(/\D/g, '');
  if (str.length < 4) return '***';
  return '*'.repeat(str.length - 4) + str.slice(-4);
}

// Helper to get birth year from date
function getBirthYear(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).getFullYear();
  } catch {
    return '—';
  }
}

export default function TalukaCitizens() {
  const [citizens, setCitizens] = useState([]);
  const [gps, setGps]           = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [page, setPage]         = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [filters, setFilters] = useState({
    tenant_id: '', gender: '', search: ''
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [cRes, gRes] = await Promise.all([
        talukaAPI.getCitizens({ page, limit: 20, ...filters }),
        gps.length === 0 ? talukaAPI.getGrampanchayats() : Promise.resolve({ data: { grampanchayats: gps } })
      ]);
      const cData = cRes.data?.citizens || cRes.data?.data || cRes.data || {};
      setCitizens(Array.isArray(cData) ? cData : []);
      setTotalPages(cRes.data?.pagination?.totalPages || Math.ceil((cRes.data?.pagination?.total || 1) / 20) || 1);
      if (gps.length === 0) setGps(gRes.data?.grampanchayats || gRes.data?.data || gRes.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load citizens');
    } finally {
      setLoading(false);
    }
  }, [page, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const setFilter = (key, val) => { setFilters(f => ({ ...f, [key]: val })); setPage(1); };

  return (
    <div>
      <div className="tk-page-header">
        <h1>Citizens</h1>
        <p>All registered citizens across Grampanchayats</p>
      </div>

      <div className="tk-filter-bar">
        <input
          placeholder="Search name, Aadhaar…"
          value={filters.search}
          onChange={e => setFilter('search', e.target.value)}
        />
        <select value={filters.tenant_id} onChange={e => setFilter('tenant_id', e.target.value)}>
          <option value="">All GPs</option>
          {gps.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <select value={filters.gender} onChange={e => setFilter('gender', e.target.value)}>
          <option value="">All Genders</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="other">Other</option>
        </select>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40 }}>Loading…</div>}
      {error   && <div style={{ textAlign: 'center', padding: 40, color: '#c62828' }}>{error}</div>}

      {!loading && !error && (
        <>
          <div className="tk-card">
            <div className="tk-table-wrap">
              <table className="tk-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Gender</th>
                    <th>Birth Year</th>
                    <th>Mobile (Masked)</th>
                    <th>Grampanchayat</th>
                    <th>Category</th>
                    <th>Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {citizens.map((c, i) => (
                    <tr key={c.id || i}>
                      <td><strong>{c.first_name} {c.last_name}</strong></td>
                      <td style={{ textTransform: 'capitalize' }}>{c.gender || '—'}</td>
                      <td>{getBirthYear(c.date_of_birth)}</td>
                      <td style={{ fontFamily: 'monospace' }}>{maskPhone(c.mobile_number)}</td>
                      <td><strong>{c.gp_name || c.Tenant?.name || '—'}</strong></td>
                      <td style={{ textTransform: 'capitalize' }}>{c.category || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {c.createdAt ? new Date(c.createdAt).toLocaleDateString('en-IN') : '—'}
                      </td>
                    </tr>
                  ))}
                  {citizens.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#aaa' }}>No citizens found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="tk-pagination">
            <button onClick={() => setPage(p => p - 1)} disabled={page <= 1}>← Prev</button>
            <span style={{ fontSize: '0.85rem', color: '#555' }}>Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>Next →</button>
          </div>
        </>
      )}
    </div>
  );
}
